import { TFunction } from "i18next";
import { Link } from "react-router-dom";

import { EventTypeCellParts, EventTypeStrategy } from "./EventTypeStrategy";
import { getTranslatedEventTypeName } from "../../utils/eventTypeUtils";

import { Button } from "@/components/ui/v2/button";
import { TransactionType } from "@/types/transaction/TransactionType";
import { formatCurrency } from "@/utils/valueFormat";

const buildLinkPart = (
  name: string | undefined,
  id: string | undefined,
  linkPath: string,
  prefix: string
): JSX.Element | null => {
  if (!name && !id) return null;

  const displayName = name || id;
  if (!displayName) return null;

  if (id && linkPath) {
    return (
      <>
        {prefix}{" "}
        <Button asChild variant={"link"} className="px-0">
          <Link to={linkPath}>{displayName}</Link>
        </Button>
      </>
    );
  }

  return (
    <>
      {prefix} <b>{displayName}</b>
    </>
  );
};

export class CampaignBudgetUpdatedEventTypeStrategy
implements EventTypeStrategy
{
  renderCell(row: TransactionType, t: TFunction): JSX.Element {
    const { campaignName, jobBoardName, month, previousAmount } = row.eventData;
    const parts: (string | JSX.Element)[] = [];

    if (campaignName)
      parts.push(" ", t("for"), " ", <b key="campaign">{campaignName}</b>);
    if (jobBoardName)
      parts.push(" ", t("on"), " ", <b key="jobboard">{jobBoardName}</b>);
    if (month) {
      const monthDate = new Date(month);
      if (!Number.isNaN(monthDate.getTime())) {
        parts.push(
          " ",
          t("in"),
          " ",
          <b key="month">
            {monthDate.toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })}
          </b>
        );
      }
    }
    if (previousAmount !== null && previousAmount !== undefined) {
      parts.push(
        " | ",
        t("common.previous"),
        " ",
        t("common.amount"),
        ": ",
        <b key="prev">{formatCurrency(previousAmount)}</b>
      );
    }

    const content = (
      <>
        {t(`eventTypes.${row.eventType}`)}
        {parts}
      </>
    );

    return <>{content}</>;
  }

  getTypeAndDescription(
    row: TransactionType,
    t: TFunction
  ): EventTypeCellParts {
    const {
      campaignName,
      jobBoardName,
      campaignId,
      clientIntegrationModuleId: clientIntegrationModuleIdFromEvent,
      integrationModuleId: integrationModuleIdFromEvent,
      jobBoardId,
      month,
      previousAmount,
    } = row.eventData || {};
    const clientIntegrationModuleId =
      clientIntegrationModuleIdFromEvent ?? integrationModuleIdFromEvent ?? jobBoardId;
    const clientId = row.eventData?.clientId || row.clientId2;

    const parts: (string | JSX.Element)[] = [];

    // Campaign part
    const campaignLink =
      campaignId && clientId
        ? `/clients/${clientId}/campaigns/${campaignId}`
        : undefined;
    const campaignPart = buildLinkPart(
      campaignName,
      campaignId,
      campaignLink || "",
      t("common.for")
    );
    if (campaignPart) parts.push(campaignPart);

    // Job board / integration part
    const jobBoardLink =
      clientIntegrationModuleId && campaignId && clientId
        ? `/clients/${clientId}/campaigns/${campaignId}/integrations/${clientIntegrationModuleId}`
        : undefined;
    const jobBoardPart = buildLinkPart(
      jobBoardName,
      clientIntegrationModuleId,
      jobBoardLink || "",
      t("common.on")
    );
    if (jobBoardPart) {
      if (parts.length > 0) parts.push(" ");
      parts.push(jobBoardPart);
    }

    // Month part
    if (month) {
      try {
        const monthDate = new Date(month);
        if (!Number.isNaN(monthDate.getTime())) {
          if (parts.length > 0) parts.push(" ");
          parts.push(
            <>
              {t("common.in")}{" "}
              <b>
                {monthDate.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </b>
            </>
          );
        }
      } catch {
        // Invalid date, skip
      }
    }

    // Amount parts
    const amountParts: (string | JSX.Element)[] = [];
    if (previousAmount !== null && previousAmount !== undefined) {
      amountParts.push(
        <>
          {t("common.previous")} {t("common.amount")}:{" "}
          <b>{formatCurrency(previousAmount)}</b>
        </>
      );
    }

    if (amountParts.length > 0) {
      if (parts.length > 0) parts.push(" | ");
      parts.push(...amountParts);
    }

    return {
      type: <>{getTranslatedEventTypeName(row.eventType, t)}</>,
      description: <>{parts}</>,
    };
  }
}
